/**
 * The bearer secret arrives in the URL fragment (`#<secret>`) — the one
 * part of a URL a server, proxy, or access log never sees. Task 7's own
 * words (docs/plans/2026-08-26-sharing-phase-2.md): the client reads
 * `location.hash` at first paint, keeps the secret in an IN-MEMORY
 * variable (never re-read from the URL), and strips the fragment from the
 * visible address bar immediately — before the network round-trip, so a
 * screenshot taken ONCE THIS MODULE HAS RUN never shows it.
 *
 * Stated precisely, per Bianca's gate on PR #83 (softening an earlier,
 * looser claim): this buys "never shows it once our JS has run", not
 * "never shows it, full stop". Between the browser painting the raw URL
 * and this module's own `boot()` call executing (network fetch of the
 * HTML, parse, `DOMContentLoaded`), the fragment IS sitting in the
 * address bar — an inherent property of a fragment-carried secret over
 * an ordinary page load, not a gap this module could close by trying
 * harder. What this module actually guarantees, and what
 * `e2e/viewer.spec.ts`'s request-time assertion actually proves: the
 * strip happens synchronously, inside `captureAndStripSecret`, before
 * this module's caller can possibly issue the first resolve request —
 * so by the time ANY network request tied to this venn leaves the
 * browser, the address bar is already clean.
 *
 * This module is the one place that reads `location.hash` and the one
 * place that calls `history.replaceState`. Everything downstream (the
 * resolve call, its retries) is handed the captured string and never
 * touches the URL again — that is what "in-memory, not re-read" means in
 * code, not just in prose.
 */

/** The two DOM primitives this module needs, kept as an interface so the
 *  capture logic is testable without a real `window` — see
 *  `test/hashSecret.test.ts`. */
export interface FragmentSource {
  /** `location.hash`, including its leading `#` (or `''` if absent).
   *  MUST be a LIVE read of the current address bar, never a value
   *  captured once and cached (Ferruccio's gate, PR #83, I2, blocking):
   *  the earlier `windowFragmentSource` below captured `win.location.hash`
   *  as a plain property AT CONSTRUCTION TIME — his probe measured
   *  `fragmentSource.hash` still holding the original secret long after
   *  `captureAndStripSecret` had "wiped" `this.secret` on the
   *  controller, because the controller's own field was never the only
   *  copy: this object had its own, un-wiped, for the lifetime of the
   *  page (surviving the closure, and a bfcache freeze, right along
   *  with it). A getter closes the gap by construction — there is
   *  nothing to wipe because there is nowhere a stale copy could be
   *  sitting; reading `.hash` after the strip simply returns the
   *  address bar's own current (already-stripped) value. */
  readonly hash: string;
  /** Strip the fragment from the visible address bar. The real adapter
   *  calls `history.replaceState(null, '', <url without hash>)`; a test
   *  double just records that it was called. */
  stripFromAddressBar(): void;
}

/**
 * Captures the fragment secret and strips it from the address bar in one
 * call — the two steps the plan requires to happen together, at first
 * paint, before anything async. Returns `null` when there was no secret
 * (an empty or absent fragment): a bare `/v/:vennId` visit with no `#` is
 * not a resolve attempt, it is a dead/malformed link from the start.
 */
export function captureAndStripSecret(source: FragmentSource): string | null {
  const raw = source.hash;
  // `location.hash` is either '' or starts with '#'; either way the
  // secret itself is everything after that leading character.
  const secret = raw.startsWith('#') ? raw.slice(1) : raw;
  source.stripFromAddressBar();
  return secret.length > 0 ? secret : null;
}

/** The production adapter: reads `window.location`, writes via
 *  `window.history.replaceState`. Kept to a few lines on purpose — all
 *  the logic worth testing lives in `captureAndStripSecret` above. */
export function windowFragmentSource(win: Window): FragmentSource {
  return {
    // A GETTER, not a captured value (see the interface's own comment
    // above) — every read reflects `win.location.hash` at THAT moment,
    // so once `stripFromAddressBar` has run, this legitimately (and
    // honestly) returns ''.
    get hash(): string {
      return win.location.hash;
    },
    stripFromAddressBar(): void {
      const stripped = win.location.pathname + win.location.search;
      win.history.replaceState(null, '', stripped);
    },
  };
}
